import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Award,
  Bell,
  BookOpen,
  Camera,
  ChevronDown,
  Check,
  CheckCircle2,
  ClipboardPen,
  Copy,
  FileText,
  MessageSquare,
  Percent,
  Plus,
  Tag,
  Video,
  Wallet,
} from "lucide-react";
import useStudentPortalFetch, {
  StudentPortalShell,
  StudentPortalState,
  formatPortalDate,
  httpUrl,
  ProgramCover,
  formatPortalMoney,
  downloadPortalCertificate,
} from "./useStudentPortalFetch";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { resolveOrganizationId } from "../../Utils/organizationSession";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import {
  STUDENT_NOTIF_READ_EVENT,
  filterUnreadStudentNotifications,
  markStudentNotificationRead,
  studentNotificationId,
} from "../../Utils/studentNotifications";

const COVER_TONES = [
  ["#1d4ed8", "#38bdf8"],
  ["#0f766e", "#2dd4bf"],
  ["#7c3aed", "#c084fc"],
  ["#c2410c", "#fb923c"],
  ["#0369a1", "#38bdf8"],
  ["#be185d", "#fb7185"],
];

function coverTone(seed) {
  const s = String(seed || "");
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash + s.charCodeAt(i) * (i + 1)) % COVER_TONES.length;
  }
  return COVER_TONES[hash];
}

function formatShortDate(value) {
  const formatted = formatPortalDate(value);
  if (!formatted) return "—";
  const match = formatted.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) return formatted;
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const month = months[Number(match[2]) - 1] || match[2];
  return `${Number(match[1])} ${month} ${match[3]}`;
}

function isCurrentLearningBatch(row) {
  const key = courseStatusMeta(row).key;
  return key === "active" || key === "pending";
}

function isCompletedLearningBatch(row) {
  return courseStatusMeta(row).key === "completed";
}

function courseSortTime(row) {
  const raw =
    row?.completed_on ||
    row?.completion_date ||
    row?.started_on ||
    row?.admission_date ||
    "";
  const time = Date.parse(String(raw));
  if (Number.isFinite(time)) return time;
  return Number(row?.organization_ems_admission_id || 0);
}

function pickDashboardLearningCourses(list) {
  const courses = Array.isArray(list) ? list.filter(Boolean) : [];
  const ongoing = courses.filter((row) => courseStatusMeta(row).key === "active");
  if (ongoing.length > 0) {
    return [...ongoing].sort((a, b) => courseSortTime(b) - courseSortTime(a)).slice(0, 1);
  }
  const scheduled = courses.filter((row) => courseStatusMeta(row).key === "pending");
  if (scheduled.length > 0) {
    return [...scheduled].sort((a, b) => courseSortTime(b) - courseSortTime(a)).slice(0, 1);
  }
  const completed = courses.filter(isCompletedLearningBatch);
  if (completed.length > 0) {
    return [...completed].sort((a, b) => courseSortTime(b) - courseSortTime(a)).slice(0, 1);
  }
  return [];
}

function courseStatusMeta(row) {
  const raw = String(row?.batch_status || "").trim();
  const lower = raw.toLowerCase().replace(/\s+/g, " ");
  if (lower === "completed" || lower === "complete" || lower === "finished") {
    return { key: "completed", label: "Completed" };
  }
  if (lower === "cancelled" || lower === "canceled") {
    return { key: "cancelled", label: "Cancelled" };
  }
  if (lower === "scheduled") {
    return { key: "pending", label: "Scheduled" };
  }
  if (lower === "ongoing" || lower === "on going") {
    return { key: "active", label: "Ongoing" };
  }
  if (raw) return { key: "neutral", label: raw };
  return { key: "active", label: "Ongoing" };
}

function moneyInr(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

const DAILY_QUOTES = [
  "Small progress every day adds up to big results.",
  "Stay curious. The more you learn, the more you grow.",
  "Skills today. A brighter tomorrow. Keep learning.",
  "Show up, practise, and the results will follow.",
  "One focused hour beats a distracted day.",
  "Growth loves consistency more than intensity.",
  "Your future self will thank you for today's effort.",
  "Progress is quieter than doubt — keep going.",
  "Every class is a step closer to the skill you want.",
  "Start where you are. Use what you know. Keep moving.",
  "Discipline turns effort into results.",
  "You don't have to be fast. You have to be steady.",
  "Good questions are how experts begin.",
  "Finish today's lesson. Momentum will do the rest.",
  "Confidence is built in the hours nobody sees.",
  "A small revision today saves a struggle tomorrow.",
  "Be proud of showing up — that is the hardest part.",
  "Knowledge compounds when you practise it.",
  "Your pace is yours. Your progress still counts.",
  "Clarity comes after the first attempt, not before.",
  "Keep the streak. Learning loves a routine.",
  "Effort is the only shortcut that actually works.",
  "Turn confusion into a question, then into a skill.",
  "Done is better than perfect — then improve it.",
  "The best time to revise is while it still feels fresh.",
  "Growth feels slow until it suddenly doesn't.",
  "Learn something small today. Repeat tomorrow.",
  "Your notes are a gift to your future self.",
  "Stay kind to yourself and serious about the work.",
  "Keep learning. Keep growing. The rest will follow.",
];

function quoteForToday(date = new Date()) {
  const day = date.getDate();
  return DAILY_QUOTES[(day - 1) % DAILY_QUOTES.length];
}

function formatWelcomeDate(date = new Date(), timeZone = null) {
  const options = {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  if (timeZone) options.timeZone = timeZone;
  try {
    const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    const weekday = get("weekday");
    const day = get("day");
    const month = get("month");
    const year = get("year");
    const hour = get("hour");
    const minute = get("minute");
    const dayPeriod = get("dayPeriod");
    const time = [hour, minute].filter(Boolean).join(":");
    const clock = dayPeriod ? `${time} ${dayPeriod}` : time;
    return `${weekday}, ${day} ${month} ${year} · ${clock}`;
  } catch {
    const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();
    const clock = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${weekday}, ${day} ${month} ${year} · ${clock}`;
  }
}

function formatMonthsLabel(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const rounded =
    Math.abs(n - Math.round(n)) < 0.001 ? String(Math.round(n)) : String(n);
  return `${rounded} ${Number(rounded) === 1 ? "Month" : "Months"}`;
}

function formatHoursLabel(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const rounded =
    Math.abs(n - Math.round(n)) < 0.001 ? String(Math.round(n)) : String(n);
  return `${rounded} ${Number(rounded) === 1 ? "Hour" : "Hours"}`;
}

function isFastTrackRow(row) {
  return /fast\s*track/i.test(String(row?.duration_track || ""));
}

function SheetLink({
  to,
  href,
  title,
  children,
  enabled = true,
  className = "ems-dash-sheet-link",
  icon: Icon,
  onClick,
  disabled = false,
}) {
  const off = !enabled || disabled;
  const classes = [
    className,
    off && className.includes("ems-dash-pill") ? "ems-dash-pill-disabled" : "",
    off && className.includes("ems-dash-notes") ? "is-empty" : "",
    off && className === "ems-dash-sheet-link" ? "is-off" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const content = (
    <>
      {Icon ? <Icon size={14} /> : null}
      {children}
    </>
  );

  if (off && !onClick) {
    return <span className={classes}>{content}</span>;
  }
  if (onClick) {
    return (
      <button
        type="button"
        className={classes}
        title={title}
        disabled={off || disabled}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }
  if (href) {
    return (
      <a
        className={classes}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={title}
      >
        {content}
      </a>
    );
  }
  if (to) {
    return (
      <Link className={classes} to={to} title={title}>
        {content}
      </Link>
    );
  }
  return <span className={classes}>{content}</span>;
}

function SheetSoon({ children }) {
  return <span className="ems-dash-sheet-soon">{children}</span>;
}

function SheetFact({ label, value, className = "", soft = false }) {
  return (
    <p className={`ems-dash-sheet-fact ${className}`.trim()}>
      <span className="ems-dash-sheet-k">{label}</span>
      <span className={`ems-dash-sheet-v${soft ? " is-soft" : ""}`}>
        {value}
      </span>
    </p>
  );
}

function notifyIcon(type) {
  const key = String(type || "").toLowerCase();
  if (key === "fee") return Wallet;
  if (key === "class") return Camera;
  if (key === "assessment") return ClipboardPen;
  if (key === "result") return Award;
  return Bell;
}

function LearningCoursesSection({
  courses,
  certBusyId,
  onCertificateDownload,
  className = "ems-dash-card ems-dash-learning-full",
}) {
  const [openDetailIds, setOpenDetailIds] = useState(() => new Set());

  const toggleDetails = (admissionId) => {
    setOpenDetailIds((prev) => {
      const next = new Set(prev);
      const key = String(admissionId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <section className={className}>
      <div className="ems-dash-card-head">
        <h2>
          <BookOpen size={18} />
          Learning
        </h2>
      </div>
      {courses.length === 0 ? (
        <p className="ems-dash-empty">
          You are not enrolled in a course yet.{" "}
          <Link to="/student/catalog">Explore courses</Link>
        </p>
      ) : (
        <div className="ems-dash-learning-list">
          <div className="ems-dash-sheet-head" aria-hidden="true">
            <span>Course</span>
            <span>Resources</span>
          </div>
          {courses.map((row) => {
            const notesUrl = httpUrl(row.notes_url);
            const interviewQaUrl = httpUrl(row.interview_qa_url);
            const classLink = httpUrl(row.class_link);
            const isFastTrack = isFastTrackRow(row);
            const hoursLabel =
              formatHoursLabel(row.duration_hours) ||
              row.duration_label ||
              null;
            const endDate =
              row.completed_on ||
              row.completion_date ||
              (isFastTrack
                ? row.estimated_batch_end_date
                : row.ends_on) ||
              null;
            const certificate = row.certificate || null;
            const certificateId =
              certificate?.organization_ems_certificate_id;
            const canDownloadCert = Boolean(
              row.can_download_certificate && certificateId
            );
            const certBusy =
              certificateId != null &&
              String(certBusyId) === String(certificateId);
            const status = courseStatusMeta(row);
            const isCompleted = status.key === "completed";
            const monthsLabel = formatMonthsLabel(row.duration_in_months);
            const detailsOpen = openDetailIds.has(
              String(row.organization_ems_admission_id)
            );

            return (
              <article
                key={row.organization_ems_admission_id}
                className={`ems-dash-sheet-card${
                  status.key === "active" ? " is-ongoing" : ""
                }`}
              >
                <div className="ems-dash-sheet-pair">
                  <div className="ems-dash-sheet-col">
                    <div className="ems-dash-sheet-title-row">
                      <strong title={row.program_name}>
                        {row.program_name}
                      </strong>
                      <span
                        className={`ems-dash-course-status is-${status.key}`}
                      >
                        {status.label}
                      </span>
                    </div>
                    <div className="ems-dash-course-meta">
                      <p className="ems-dash-course-category">
                        {row.category_name || "—"}
                      </p>
                      <SheetFact
                        className="ems-dash-sheet-muted"
                        label="Batch Code"
                        value={row.batch_code || row.batch_name || "—"}
                        soft
                      />
                    </div>
                    {detailsOpen ? (
                      <>
                        <SheetFact
                          label="Admission Date"
                          value={formatShortDate(row.admission_date)}
                        />
                        <SheetFact
                          className="ems-dash-sheet-muted"
                          label="Admission No"
                          value={row.admission_number || "—"}
                          soft
                        />
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="ems-dash-sheet-col ems-dash-sheet-resources">
                  <SheetLink
                    href={notesUrl}
                    enabled={Boolean(notesUrl)}
                    className="ems-dash-notes"
                    icon={FileText}
                  >
                    Notes
                  </SheetLink>
                  <SheetLink
                    to="/student/assignments"
                    className="ems-dash-notes"
                    icon={ClipboardPen}
                  >
                    Assessments
                  </SheetLink>
                  <SheetLink
                    to="/student/class-records"
                    className="ems-dash-notes"
                    icon={Video}
                  >
                    Recordings
                  </SheetLink>
                  <SheetLink
                    href={interviewQaUrl}
                    enabled={Boolean(interviewQaUrl)}
                    className="ems-dash-notes"
                    icon={MessageSquare}
                  >
                    Interview Q/A
                  </SheetLink>
                </div>

                <div className="ems-dash-sheet-footer">
                  <div className="ems-dash-sheet-actions">
                    {isCompleted ? (
                      canDownloadCert ? (
                        <SheetLink
                          enabled
                          className="ems-dash-pill ems-dash-pill-blue"
                          icon={Award}
                          disabled={certBusy}
                          onClick={() => onCertificateDownload(row)}
                        >
                          {certBusy ? "Downloading…" : "Certificate"}
                        </SheetLink>
                      ) : (
                        <span className="ems-dash-pill ems-dash-pill-disabled">
                          <Award size={14} />
                          Certificate
                        </span>
                      )
                    ) : (
                      <SheetLink
                        href={classLink}
                        enabled={Boolean(classLink)}
                        title={row.class_time_label || "Join class"}
                        className="ems-dash-pill ems-dash-pill-blue"
                        icon={Camera}
                      >
                        Join Class
                      </SheetLink>
                    )}
                  </div>
                  <div className="ems-dash-sheet-links-row">
                    <button
                      type="button"
                      className={`ems-dash-see-more${
                        detailsOpen ? " is-open" : ""
                      }`}
                      aria-expanded={detailsOpen}
                      onClick={() =>
                        toggleDetails(row.organization_ems_admission_id)
                      }
                    >
                      {detailsOpen ? "Less Details" : "More Details"}
                      <ChevronDown size={14} />
                    </button>
                    <SheetLink
                      to="/student/courses"
                      className="ems-dash-notes ems-dash-all-courses"
                    >
                      My All Courses
                    </SheetLink>
                  </div>
                  {detailsOpen ? (
                    <div className="ems-dash-sheet-col ems-dash-duration-col">
                      <span className="ems-dash-duration-heading">
                        Duration
                      </span>
                      <span className="ems-dash-duration-hours">
                        {hoursLabel || "—"}
                      </span>
                      <p className="ems-dash-sheet-pace">
                        {row.duration_track ? (
                          <SheetSoon>{row.duration_track}</SheetSoon>
                        ) : null}
                        {isFastTrack && monthsLabel ? (
                          <span className="ems-dash-sheet-months">
                            {row.duration_track ? " | " : ""}
                            {monthsLabel}
                          </span>
                        ) : null}
                        {isFastTrack && hoursLabel ? (
                          <span className="ems-dash-sheet-months">
                            {row.duration_track || monthsLabel ? " | " : ""}
                            {hoursLabel}
                          </span>
                        ) : !isFastTrack && monthsLabel ? (
                          <span className="ems-dash-sheet-months">
                            {row.duration_track ? " | " : ""}
                            {monthsLabel}
                          </span>
                        ) : null}
                      </p>
                      <div className="ems-dash-duration-more">
                        <SheetFact
                          label="Started On"
                          value={formatShortDate(row.started_on)}
                        />
                        <SheetFact
                          label={
                            row.completed_on || row.completion_date
                              ? "Ended on"
                              : isFastTrack
                                ? "Estimated Batch End Date"
                                : "Ending on"
                          }
                          value={formatShortDate(endDate)}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function DashboardNotifications({ notifications, orgId, studentId }) {
  const [rows, setRows] = useState(() =>
    filterUnreadStudentNotifications(notifications, orgId, studentId)
  );

  useEffect(() => {
    setRows(filterUnreadStudentNotifications(notifications, orgId, studentId));
  }, [notifications, orgId, studentId]);

  useEffect(() => {
    const onRead = () => {
      setRows(filterUnreadStudentNotifications(notifications, orgId, studentId));
    };
    window.addEventListener(STUDENT_NOTIF_READ_EVENT, onRead);
    return () => window.removeEventListener(STUDENT_NOTIF_READ_EVENT, onRead);
  }, [notifications, orgId, studentId]);

  const handleOpen = (row) => {
    markStudentNotificationRead(orgId, studentId, row);
    const readId = studentNotificationId(row);
    setRows((prev) => prev.filter((n) => studentNotificationId(n) !== readId));
  };

  return (
    <section className="ems-dash-card ems-dash-notify-card">
      <div className="ems-dash-card-head">
        <h2>
          <Bell size={18} />
          Notifications
        </h2>
      </div>
      {rows.length === 0 ? (
        <p className="ems-dash-empty">No new notifications.</p>
      ) : (
        <ul className="ems-dash-notify">
          {rows.map((row, index) => {
            const Icon = notifyIcon(row.type);
            const href = row.href || "/student";
            return (
              <li key={`${row.type || "note"}-${row.id || index}`}>
                <Link to={href} onClick={() => handleOpen(row)}>
                  <span
                    className={`ems-dash-notify-icon is-${row.type || "class"}`}
                  >
                    <Icon size={16} />
                  </span>
                  <span>
                    <strong>{row.title || "Notification"}</strong>
                    {row.text ? <em>{row.text}</em> : null}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function ProgressRing({ percent, label, hint, tone }) {
  const p = Math.max(0, Math.min(100, Number(percent) || 0));
  return (
    <div className="ems-dash-progress-item">
      <div
        className={`ems-dash-ring ems-dash-ring-${tone}`}
        style={{ "--p": p }}
        aria-label={`${label} ${p}%`}
      >
        <span>{p}%</span>
      </div>
      <div>
        <strong>{label}</strong>
        <p>{hint}</p>
      </div>
    </div>
  );
}

export default function StudentDashboardPage() {
  const { data, loading, error, refetch } = useStudentPortalFetch("dashboard");
  const userData = useAuthStore((s) => s.userData);
  const orgId = resolveOrganizationId(userData);
  const loginuser = Array.isArray(userData?.loginuser)
    ? userData.loginuser[0]
    : userData?.loginuser;
  const studentId =
    loginuser?.organization_ems_student_id ||
    userData?.organization_ems_student_id ||
    data?.student?.organization_ems_student_id ||
    "";
  const [now, setNow] = useState(() => new Date());
  const [certBusyId, setCertBusyId] = useState(null);
  const [claimBusyId, setClaimBusyId] = useState(null);
  const [claimNote, setClaimNote] = useState("");
  const [openOfferId, setOpenOfferId] = useState(null);
  const [copiedOfferId, setCopiedOfferId] = useState(null);

  useEffect(() => {
    const tick = () => setNow(new Date());
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, []);

  const student = data?.student || {};
  const allCourses = Array.isArray(data?.courses) ? data.courses : [];
  const courses = pickDashboardLearningCourses(allCourses);
  const offers = Array.isArray(data?.offers) ? data.offers : [];
  const notifications = Array.isArray(data?.notifications)
    ? data.notifications
    : [];
  const stats = data?.stats || {};
  const progress = data?.progress || {};
  const firstName = student.first_name || "there";
  const displayTimezone =
    data?.display_timezone || student.display_timezone || null;
  const feeDueThisMonth = Number(stats.fee_due_this_month || 0);
  const feeDueCode = String(
    stats.fee_due_currency || stats.display_currency || "INR"
  )
    .trim()
    .toUpperCase() || "INR";
  const feeDueAmount = Number.isFinite(feeDueThisMonth)
    ? feeDueThisMonth.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    : "0";
  const feeDueParts = { code: feeDueCode, amount: feeDueAmount };
  // Always format live clock in the student's timezone (do not rely on static API date-only label).
  const todayLabel = formatWelcomeDate(now, displayTimezone);

  const handleCertificateDownload = async (row) => {
    const certificate = row?.certificate || null;
    const certificateId = certificate?.organization_ems_certificate_id;
    if (!certificateId || !row?.can_download_certificate) return;
    setCertBusyId(certificateId);
    try {
      await downloadPortalCertificate(
        certificateId,
        certificate.certificate_number ||
          certificate.certificate_name ||
          "certificate",
        certificate.certificate_url
      );
    } catch {
      /* ignore — portal download helper surfaces browser save */
    } finally {
      setCertBusyId(null);
    }
  };

  const copyOfferCode = async (offerId, code) => {
    const text = String(code || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const input = document.createElement("textarea");
      input.value = text;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.left = "-9999px";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopiedOfferId(offerId);
    window.setTimeout(() => {
      setCopiedOfferId((current) => (current === offerId ? null : current));
    }, 1600);
  };

  const handleClaimOffer = async (offerId) => {
    if (!offerId) return;
    setClaimBusyId(offerId);
    setClaimNote("");
    try {
      const res = await axios.post(
        `${MAIN_URL}/api/ems/portal/offers/${offerId}/claim`,
        {},
        {
          headers: getApiHeaders(),
          params: orgId ? { organization_id: orgId } : {},
        }
      );
      setClaimNote(res?.data?.message || "Offer claimed.");
      await refetch();
    } catch (err) {
      setClaimNote(
        err?.response?.data?.message || "Could not claim this offer."
      );
    } finally {
      setClaimBusyId(null);
    }
  };

  return (
    <StudentPortalShell hideHeader className="ems-student-dashboard">
      <StudentPortalState loading={loading} error={error}>
        <section className="ems-dash-welcome">
          <div>
            <h1>
              {data?.greeting || "Hello"}, {firstName}!{" "}
              <span aria-hidden="true">👋</span>
            </h1>
            <p>Keep learning. Keep growing.</p>
          </div>
          <div className="ems-dash-welcome-aside">
            <time dateTime={now.toISOString()}>{todayLabel}</time>
            <blockquote>
              “{data?.quote || quoteForToday(now)}”
            </blockquote>
          </div>
        </section>

        <section className="ems-dash-stats" aria-label="This month summary">
          <article className="ems-dash-stat ems-card ems-stat">
            <span className="ems-dash-stat-icon ems-stat-icon is-course">
              <BookOpen size={17} />
            </span>
            <div className="ems-stat-label">Courses enrolled</div>
            <div className="ems-stat-value">
              {Number(stats.courses_enrolled ?? allCourses.length)}
            </div>
          </article>
          <article className="ems-dash-stat ems-card ems-stat">
            <span className="ems-dash-stat-icon ems-stat-icon is-fee">
              <Wallet size={17} />
            </span>
            <div className="ems-stat-label">Fee due this month</div>
            <div className="ems-stat-value ems-stat-money">
              <span className="ems-stat-currency">{feeDueParts.code}</span>{" "}
              <span className="ems-stat-amount">{feeDueParts.amount}</span>
            </div>
          </article>
          <article className="ems-dash-stat ems-card ems-stat">
            <span className="ems-dash-stat-icon ems-stat-icon is-assessment">
              <ClipboardPen size={17} />
            </span>
            <div className="ems-stat-label">Assessments due this month</div>
            <div className="ems-stat-value">{Number(stats.assessments_due_this_month || 0)}</div>
          </article>
          <article className="ems-dash-stat ems-card ems-stat">
            <span className="ems-dash-stat-icon ems-stat-icon is-attendance">
              <Percent size={17} />
            </span>
            <div className="ems-stat-label">Attendance this month</div>
            <div className="ems-stat-value">{Number(stats.attendance_percent_this_month || 0)}%</div>
          </article>
        </section>

        <div className="ems-dash-layout">
          <LearningCoursesSection
            courses={courses}
            certBusyId={certBusyId}
            onCertificateDownload={handleCertificateDownload}
            className="ems-dash-card ems-dash-learning-card-panel"
          />
          <div className="ems-dash-split-side">
            <Link to="/student/catalog" className="ems-dash-add-course-btn">
              <Plus size={18} />
              Add new course
            </Link>
            <DashboardNotifications
              notifications={notifications}
              orgId={orgId}
              studentId={studentId}
            />
          </div>

          <section className="ems-dash-card ems-dash-offers-card">
              <div className="ems-dash-card-head">
                <h2>
                  <Tag size={18} />
                  Offers for You
                </h2>
              </div>
              {claimNote ? <p className="ems-dash-empty">{claimNote}</p> : null}
              {offers.length === 0 ? (
                <p className="ems-dash-empty">No offers right now.</p>
              ) : (
                <div className="ems-portal-offers">
                  {offers.map((row) => {
                    const [from, to] = coverTone(`offer-${row.offer_name || row.program_name}`);
                    const hasPrice = Number(row.original_amount) > 0;
                    const claimed = row.redemption_status === "CLAIMED" || row.redemption_status === "APPLIED";
                    const discount = row.discount_label || row.badge || "Offer";
                    const description =
                      row.description ||
                      `${discount} on ${row.program_name || "this program"}.`;
                    const canExpand = String(description).trim().length > 110;
                    const detailsOpen = openOfferId === row.organization_ems_offer_id;
                    const codeCopied = copiedOfferId === row.organization_ems_offer_id;
                    return (
                      <article
                        key={row.organization_ems_offer_id}
                        className="ems-portal-offer"
                      >
                        <ProgramCover
                          src={row.photo_url}
                          alt={row.offer_name || row.program_name}
                          className="ems-portal-offer-media"
                          style={{
                            background: `linear-gradient(135deg, ${from}, ${to})`,
                          }}
                        >
                          <span className="ems-portal-offer-mark">{discount}</span>
                        </ProgramCover>
                        <div className="ems-portal-offer-body">
                          <div className="ems-portal-offer-top">
                            <span className="ems-dash-offer-badge">{discount}</span>
                            {row.offer_code ? (
                              <button
                                type="button"
                                className="ems-portal-offer-code"
                                onClick={() =>
                                  copyOfferCode(
                                    row.organization_ems_offer_id,
                                    row.offer_code
                                  )
                                }
                                aria-label={
                                  codeCopied
                                    ? "Offer code copied"
                                    : "Copy offer code"
                                }
                              >
                                {row.offer_code}
                                {codeCopied ? <Check size={12} /> : <Copy size={12} />}
                              </button>
                            ) : null}
                          </div>
                          <strong>{row.offer_name || row.program_name}</strong>
                          {row.program_name ? (
                            <span className="ems-portal-offer-program">{row.program_name}</span>
                          ) : null}
                          <p className={detailsOpen ? "is-open" : undefined}>{description}</p>
                          {canExpand ? (
                            <button
                              type="button"
                              className="ems-portal-offer-more"
                              onClick={() =>
                                setOpenOfferId(
                                  detailsOpen ? null : row.organization_ems_offer_id
                                )
                              }
                            >
                              {detailsOpen ? "See less" : "See more"}
                            </button>
                          ) : null}
                          <div className="ems-portal-offer-foot">
                            <div className="ems-portal-offer-meta">
                              {hasPrice ? (
                                <em className="ems-dash-offer-price">
                                  {Number(row.original_amount) > Number(row.final_amount) ? (
                                    <s>{formatPortalMoney(row.original_amount, "INR")}</s>
                                  ) : null}
                                  <span>Pay {formatPortalMoney(row.final_amount, "INR")}</span>
                                </em>
                              ) : (
                                <em className="ems-dash-offer-price">{discount}</em>
                              )}
                              <small>
                                {row.valid_from_label ? `From ${row.valid_from_label}` : "Available now"}
                                {row.valid_until_label ? ` · until ${row.valid_until_label}` : ""}
                              </small>
                            </div>
                            <button
                              type="button"
                              className="ems-catalog-buy-now"
                              disabled={claimed || claimBusyId === row.organization_ems_offer_id}
                              onClick={() => handleClaimOffer(row.organization_ems_offer_id)}
                            >
                              {claimed
                                ? "Claimed"
                                : claimBusyId === row.organization_ems_offer_id
                                  ? "Claiming..."
                                  : "Claim offer"}
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

          <section className="ems-dash-card ems-dash-progress-card">
              <div className="ems-dash-card-head">
                <h2>Your Progress</h2>
              </div>
              <div className="ems-dash-progress">
                <ProgressRing
                  percent={progress.attendance_percent}
                  label="Attendance"
                  hint={progress.attendance_label || "Attendance"}
                  tone="green"
                />
              </div>
              <div
                className={`ems-dash-track ${
                  progress.on_track ? "is-good" : "is-warn"
                }`}
              >
                <CheckCircle2 size={16} />
                {progress.on_track
                  ? "You're on track! Keep going to achieve your goals."
                  : "Stay consistent — a little progress each day adds up."}
              </div>
            </section>
        </div>
      </StudentPortalState>
    </StudentPortalShell>
  );
}
